import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { CartProductType } from "./cart-product-types";
import {
  ButtonSettingPerProduct,
  CoverageText,
  NonCoverageButtonText,
  TitleSubtitle,
} from "./widget";

type CartValuesForTemplate = {
  currency: Currency;
  coveragePrice: number;
  cartTotal: number;
  cartTotalPreDiscount: number;
  dueLaterTotal: number;
};

export class StorefrontCheckoutTextUtils {
  private static fillTemplateVariables({
    text,
    currency,
    coveragePrice,
    cartTotal,
    cartTotalPreDiscount,
    dueLaterTotal,
  }: CartValuesForTemplate & { text: string }): string {
    let textWithVariables = text.replaceAll(
      "%price%",
      moneyFormat({ amount: coveragePrice, currency }),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%cartTotal%",
      moneyFormat({ amount: cartTotal - dueLaterTotal, currency }),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%combinedPrice%",
      moneyFormat({
        amount: coveragePrice + cartTotal - dueLaterTotal,
        currency,
      }),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%cartTotalPreDiscount%",
      moneyFormat({ amount: cartTotalPreDiscount, currency }),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%combinedPricePreDiscount%",
      moneyFormat({ amount: cartTotalPreDiscount + coveragePrice, currency }),
    );

    return textWithVariables;
  }

  static getTextByProduct(
    textByProduct: NonCoverageButtonText | undefined,
    {
      mainProductType,
      hasProtectionReturnsAndSplit,
      cartEligibleProtection,
      cartEligibleReturns,
      cartEligibleFinalSaleReturns,
      currency,
      coveragePrice,
      cartTotal,
      cartTotalPreDiscount,
      dueLaterTotal,
    }: {
      mainProductType: Exclude<
        CartProductType,
        CartProductType.ExtendedWarranty
      >;
      hasProtectionReturnsAndSplit: boolean;
      cartEligibleReturns: boolean;
      cartEligibleFinalSaleReturns: boolean;
      cartEligibleProtection: boolean;
    } & CartValuesForTemplate,
  ): string | undefined {
    if (!textByProduct) {
      return undefined;
    }

    const { returns, packageProtection, both } = textByProduct;

    let finalText: string | undefined;
    switch (mainProductType) {
      case "return": {
        finalText = returns;

        break;
      }
      case "return,package_protection": {
        finalText = both;

        break;
      }
      case "package_protection": {
        finalText = packageProtection;

        break;
      }
      // No default
    }

    if (hasProtectionReturnsAndSplit) {
      if (
        (cartEligibleReturns || cartEligibleFinalSaleReturns) &&
        cartEligibleProtection
      ) {
        finalText = both;
      } else if (cartEligibleProtection) {
        finalText = packageProtection;
      } else {
        finalText = returns;
      }
    }

    if (finalText) {
      finalText = this.fillTemplateVariables({
        text: finalText,
        currency,
        coveragePrice,
        cartTotal,
        cartTotalPreDiscount,
        dueLaterTotal,
      });
    }

    return finalText;
  }

  static getCoverageTextAndSubtext(
    coverageText: CoverageText | undefined,
    textInfo: {
      mainProductType: Exclude<
        CartProductType,
        CartProductType.ExtendedWarranty
      >;
      hasProtectionReturnsAndSplit: boolean;
      cartEligibleReturns: boolean;
      cartEligibleFinalSaleReturns: boolean;
      cartEligibleProtection: boolean;
    } & CartValuesForTemplate,
  ): TitleSubtitle {
    if (!coverageText) {
      return {};
    }

    const titleText: string | undefined = this.getTextByProduct(
      {
        returns: coverageText.returns?.titleText,
        packageProtection: coverageText.packageProtection?.titleText,
        both: coverageText.both?.titleText,
      },
      textInfo,
    );
    const subtitleText: string | undefined = this.getTextByProduct(
      {
        returns: coverageText.returns?.subtitleText,
        packageProtection: coverageText.packageProtection?.subtitleText,
        both: coverageText.both?.subtitleText,
      },
      textInfo,
    );

    return { titleText, subtitleText };
  }

  private static defaultCheckoutButtonTexts: TitleSubtitle = {
    titleText: "Checkout+ | %combinedPrice%",
    subtitleText: "",
  };

  /** Source of truth for storefront default text when the merchants' texts are empty. Also for AB test placeholders & coverage preview defaults. */
  static defaultCheckoutTexts: {
    coverageButtonText: CoverageText;
    nonCoverageButtonText: NonCoverageButtonText;
    cardText: CoverageText;
  } = {
    coverageButtonText: {
      returns: this.defaultCheckoutButtonTexts,
      packageProtection: this.defaultCheckoutButtonTexts,
      both: this.defaultCheckoutButtonTexts,
    },
    nonCoverageButtonText: {
      returns: "Checkout without free returns",
      packageProtection: "Checkout without package protection",
      both: "Checkout without free returns & package protection",
    },

    cardText: {
      returns: {
        titleText: "Checkout+",
        subtitleText: "Free returns on your order",
      },
      packageProtection: {
        titleText: "Checkout+",
        subtitleText: "Package protection on your order",
      },
      both: {
        titleText: "Checkout+",
        subtitleText: "Free returns + package protection",
      },
    },
  };

  static buttonSettingsPerProductToCoverageButtonText: Record<
    ButtonSettingPerProduct,
    keyof typeof StorefrontCheckoutTextUtils.defaultCheckoutTexts.coverageButtonText
  > = {
    [ButtonSettingPerProduct.RETURN]: "returns",
    [ButtonSettingPerProduct.PACKAGE_PROTECTION]: "packageProtection",
    [ButtonSettingPerProduct.BOTH]: "both",
  };

  static getDefaultInputTexts(
    buttonSettingPerProduct: ButtonSettingPerProduct,
  ): {
    defaultCoverageButtonTextToUse: TitleSubtitle;
    defaultNonCoverageButtonTextToUse: string;
    defaultCardTextToUse: TitleSubtitle;
  } {
    const defaultCoverageButtonTextToUse =
      this.defaultCheckoutTexts.coverageButtonText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];
    const defaultNonCoverageButtonTextToUse =
      this.defaultCheckoutTexts.nonCoverageButtonText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];
    const defaultCardTextToUse =
      this.defaultCheckoutTexts.cardText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];

    /** We can guarantee the default texts are not undefined because we have a default text for each button setting per product */
    return {
      defaultCoverageButtonTextToUse: defaultCoverageButtonTextToUse!,
      defaultNonCoverageButtonTextToUse: defaultNonCoverageButtonTextToUse!,
      defaultCardTextToUse: defaultCardTextToUse!,
    };
  }
}
